import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Tag, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

const conditionLabels = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

const conditionColors = {
  new: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  like_new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  good: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  fair: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  poor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function ListingCard({ listing, onSave, isSaved = false }) {
  const { theme } = useTheme();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = listing.images || [];
  const hasMultipleImages = images.length > 1;
  const currentImage = images[currentImageIndex]?.url;

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSave) {
      onSave(listing.id, !isSaved);
    }
  };

  return (
    <Link
      to={`/marketplace/listing/${listing.id}`}
      className={`block rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow ${theme.card}`}
    >
      <div className="relative group">
        {currentImage ? (
          <img
            src={resolveImageUrl(currentImage)}
            alt={listing.title}
            className="w-full h-48 object-cover"
          />
        ) : (
          <div className={`w-full h-48 ${theme.backgroundSecondary} flex flex-col items-center justify-center`}>
            <Package className={`w-12 h-12 ${theme.textMuted}`} />
            <span className={`mt-2 text-sm ${theme.textMuted}`}>No Image</span>
          </div>
        )}

        {/* Carousel Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Dot Indicators */}
        {hasMultipleImages && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-4'
                    : 'bg-white/60 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        )}

        {listing.status === 'sold' && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white text-2xl font-bold">SOLD</span>
          </div>
        )}
        <button
          onClick={handleSaveClick}
          className={`absolute top-2 right-2 p-2 rounded-full ${theme.card} shadow-md hover:shadow-lg transition-all`}
        >
          <Heart
            className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : theme.textMuted}`}
          />
        </button>
        {listing.isNegotiable && (
          <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-medium ${theme.backgroundSecondary} ${theme.text} rounded-full`}>
            OBO
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className={`font-semibold text-lg truncate flex-1 ${theme.text}`}>
            {listing.title}
          </h3>
        </div>

        <p className={`text-xl font-bold ${theme.link} mb-2`}>
          {formatPrice(listing.price)}
        </p>

        <div className="flex flex-wrap gap-2 mb-3">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              conditionColors[listing.condition] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {conditionLabels[listing.condition] || listing.condition}
          </span>
          {listing.category && (
            <span className={`px-2 py-1 text-xs font-medium ${theme.backgroundSecondary} ${theme.text} rounded-full flex items-center gap-1`}>
              <Tag className="w-3 h-3" />
              {listing.category.categoryName}
            </span>
          )}
        </div>

        {(listing.locationText || listing.distance_km) && (
          <div className={`flex items-center text-sm ${theme.textSecondary}`}>
            <MapPin className="w-4 h-4 mr-1" />
            {listing.distance_km ? (
              <span className="truncate">
                {listing.distance_km} km away
                {listing.locationText && ` • ${listing.locationText}`}
              </span>
            ) : (
              <span className="truncate">{listing.locationText}</span>
            )}
          </div>
        )}

        {listing.seller && (
          <div className={`mt-3 pt-3 border-t ${theme.border} flex items-center`}>
            <div className={`w-8 h-8 rounded-full ${theme.gradient} flex items-center justify-center`}>
              <span className="text-sm font-bold text-white">
                {listing.seller.firstName?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <span className={`ml-2 text-sm ${theme.textSecondary}`}>
              {listing.seller.firstName} {listing.seller.lastName}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
